// src/hooks/useViewMode.js — selectable phone / desktop layout.
//
// The customer picks one of three display modes (Menu → Display, or Settings):
//   auto     – phone layout on narrow screens (< 1024px, Tailwind `lg`), desktop otherwise
//   mobile   – always the phone layout (single column, bottom tab bar, sheets)
//   desktop  – always the desktop three-pane layout. On a phone the viewport is
//              widened so the page renders like a zoomable desktop window.
//
// The choice is kept per browser in localStorage so it sticks across logins,
// and the resolved mode is mirrored to <html data-view="mobile|desktop"> so
// styles/mobile.css can reflow pages that were only built for desktop.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const VIEW_MODE_KEY = "cbc_view_mode";
export const VIEW_MODES = [
  { value: "auto", label: "Auto (fit my screen)" },
  { value: "mobile", label: "Phone layout" },
  { value: "desktop", label: "Desktop layout" },
];

const NARROW_QUERY = "(max-width: 1023px)";
const DESKTOP_VIEWPORT = "width=1200, initial-scale=0.32, minimum-scale=0.25, maximum-scale=3";
const DEFAULT_VIEWPORT = "width=device-width, initial-scale=1, viewport-fit=cover";

export function readViewMode() {
  try {
    const v = String(localStorage.getItem(VIEW_MODE_KEY) || "auto");
    return ["auto", "mobile", "desktop"].includes(v) ? v : "auto";
  } catch {
    return "auto";
  }
}

function isNarrowScreen() {
  try {
    return window.matchMedia(NARROW_QUERY).matches;
  } catch {
    return false;
  }
}

// Phones report a coarse pointer; a narrow desktop window does not. Used only
// to decide whether the "desktop" choice needs the widened viewport.
export function isTouchDevice() {
  try {
    return window.matchMedia("(pointer: coarse)").matches || navigator.maxTouchPoints > 0;
  } catch {
    return false;
  }
}

function setViewportMeta(content) {
  try {
    let meta = document.querySelector('meta[name="viewport"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "viewport";
      document.head.appendChild(meta);
    }
    if (meta.getAttribute("content") !== content) meta.setAttribute("content", content);
  } catch {
    /* ignore */
  }
}

const ViewModeContext = createContext({
  mode: "auto",
  setMode: () => {},
  isMobile: false,
  isNarrow: false,
});

export function ViewModeProvider({ children }) {
  const [mode, setModeState] = useState(readViewMode);
  const [narrow, setNarrow] = useState(isNarrowScreen);

  // Track the screen width for auto mode (rotation, window resize).
  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia(NARROW_QUERY);
    } catch {
      return undefined;
    }
    const onChange = (e) => setNarrow(!!e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Keep tabs in sync when the choice changes in another tab.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === VIEW_MODE_KEY) setModeState(readViewMode());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setMode = useCallback((next) => {
    const v = ["auto", "mobile", "desktop"].includes(next) ? next : "auto";
    setModeState(v);
    try {
      localStorage.setItem(VIEW_MODE_KEY, v);
    } catch {
      /* ignore */
    }
  }, []);

  const isMobile = mode === "mobile" || (mode === "auto" && narrow);

  // Mirror the resolved mode to <html> for the CSS reflow rules, and widen the
  // viewport when a phone user insists on the desktop layout.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-view", isMobile ? "mobile" : "desktop");
    root.classList.toggle("cbc-mobile", isMobile);
    const wantWide = mode === "desktop" && isTouchDevice() && window.screen && window.screen.width < 1024;
    setViewportMeta(wantWide ? DESKTOP_VIEWPORT : DEFAULT_VIEWPORT);
    return () => {
      root.removeAttribute("data-view");
      root.classList.remove("cbc-mobile");
    };
  }, [isMobile, mode]);

  const value = useMemo(() => ({ mode, setMode, isMobile, isNarrow: narrow }), [mode, setMode, isMobile, narrow]);
  return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>;
}

export default function useViewMode() {
  return useContext(ViewModeContext);
}
